import React, { FC, useState, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useRoiQuery } from "../../graphql/query";
import { useSettings } from "../../hooks";
import { Form, Cascader, Select, Row, Col, Switch, DatePicker } from "antd";
import {
  ORGANISATIONAL_UNITS_QUERY,
  OFFICE_LOCATIONS_QUERY,
  GET_CURRENCIES,
} from "../../graphql/query";
import {
  FindOrganisationalUnitsQuery,
  FindOfficeLocationsQuery,
  TimeSeriesRange,
} from "~/gql/graphql";
import { Roi, RoiTimeSeriesData } from "@design-system";
import moment from "moment";
import { VPDLabel } from "~/context/taxonomy-context";

const { RangePicker } = DatePicker;

const TimeSeriesRangeDisplayNames = {
  [TimeSeriesRange.AllTime]: "All Time",
  [TimeSeriesRange.Last12Months]: "Last 12 Months",
  [TimeSeriesRange.Last6Months]: "Last 6 Months",
  [TimeSeriesRange.Last3Months]: "Last 3 Months",
  [TimeSeriesRange.LastMonth]: "Last Month",
  [TimeSeriesRange.SinceVpd]: "Since Installing VPD",
};

function validateNestedObject(obj: any): boolean {
  return (
    obj &&
    typeof obj.averageDurationInMinutes === "number" &&
    typeof obj.averageCharge === "number" &&
    typeof obj.averageCost === "number" &&
    typeof obj.averageRecovered === "number" &&
    typeof obj.gpm === "number" &&
    typeof obj.writeOff === "number" &&
    typeof obj.writeOffPercentage === "number"
  );
}

function validateRoiTimeSeriesData(data: any): data is RoiTimeSeriesData {
  return (
    data &&
    typeof data.timeLabelString === "string" &&
    validateNestedObject(data.external) &&
    (data.vpd === null || validateNestedObject(data.vpd)) &&
    (data.notVpd === null || validateNestedObject(data.notVpd)) &&
    typeof data.externalMatterCount === "number" &&
    typeof data.vpdMatterCount === "number"
  );
}

export const RoiContainer: FC = () => {
  const [practiceAreaId, setPracticeAreaId] = useState<number | null>(null);
  const [officeLocationId, setOfficeLocationId] = useState<number | null>(null);
  const [currencyId, setCurrencyId] = useState<number | null>(null);
  const [currencySymbol, setCurrencySymbol] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeSeriesRange>(TimeSeriesRange.SinceVpd);
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);
  const [useCustomRange, setUseCustomRange] = useState<boolean>(false);
  const [form] = Form.useForm();

  const PA_LABEL = VPDLabel("PracticeArea");

  const { data: currencies } = useQuery(GET_CURRENCIES);
  const isMultiCurrencyInstance = (currencies?.currencies?.length ?? 0) > 1;

  const { settings } = useSettings();
  const defaultCurrencySymbol = settings?.currency.symbol || "";

  const isFiltered = useMemo(() => {
    return (
      practiceAreaId !== null ||
      officeLocationId !== null ||
      (isMultiCurrencyInstance && currencyId !== null) ||
      timeRange !== TimeSeriesRange.SinceVpd ||
      (startDate !== null && endDate !== null)
    );
  }, [
    practiceAreaId,
    officeLocationId,
    currencyId,
    timeRange,
    startDate,
    endDate,
    isMultiCurrencyInstance,
  ]);

  useEffect(() => {
    if (isMultiCurrencyInstance && settings?.currency.id) {
      setCurrencyId(settings.currency.id);
    }
    setCurrencySymbol(settings?.currency.symbol ?? null);
  }, [settings]);

  useEffect(() => {
    form.setFieldsValue({ currency: currencyId });
    setCurrencySymbol(settings?.currency.symbol ?? null);
  }, [currencyId]);

  const { data: organisationalUnitData } = useQuery<FindOrganisationalUnitsQuery>(
    ORGANISATIONAL_UNITS_QUERY
  );
  const practiceAreas = organisationalUnitData?.organisationalUnits || [];
  const { data: officeLocationData } = useQuery<FindOfficeLocationsQuery>(OFFICE_LOCATIONS_QUERY);
  const officeLocations = officeLocationData?.officeLocations || [];

  // Unfiltered query (always runs)
  const { loading: loadingUnfiltered, data: unfilteredQueryData } = useRoiQuery({
    timeRange: TimeSeriesRange.SinceVpd,
  });

  // Filtered query
  const { loading: loadingFiltered, data: filteredQueryData } = useRoiQuery(
    {
      filter: {
        organisationalUnitId: practiceAreaId,
        officeLocationId: officeLocationId,
        ...(isMultiCurrencyInstance && currencyId !== null && { currencyId: currencyId }),
      },
      timeRange: timeRange,
      startEndDate:
        startDate !== null && endDate !== null
          ? { startDate: startDate.toDate(), endDate: endDate.toDate() }
          : undefined,
    },
    { skip: !isFiltered }
  );

  const roiTimeSeriesData = useMemo(() => {
    const dataToUse = isFiltered ? filteredQueryData : unfilteredQueryData;
    return parseRoiTimeSeriesData(dataToUse?.roiQuery.roiTimeSeriesDataJSON ?? null);
  }, [isFiltered, filteredQueryData, unfilteredQueryData]);

  const unfilteredRoiData = useMemo(() => {
    return parseRoiTimeSeriesData(unfilteredQueryData?.roiQuery.roiTimeSeriesDataJSON ?? null);
  }, [unfilteredQueryData]);

  const paOptions = practiceAreas
    .filter((x) => !x.parent?.id)
    .map((pa) => {
      return {
        value: pa.id,
        label: pa.name,
        children:
          practiceAreas
            .filter((x) => x.parent?.id === pa.id)
            .map((y) => ({ value: y.id, label: y.name })) || [],
      };
    });

  const olOptions = officeLocations.map((ol) => {
    return {
      value: ol.id,
      label: ol.name,
    };
  });

  const timeIntervals = useMemo(() => {
    const dataToUse = isFiltered ? filteredQueryData : unfilteredQueryData;
    return dataToUse?.roiQuery.timeIntervalsJSON
      ? JSON.parse(dataToUse.roiQuery.timeIntervalsJSON)
      : null;
  }, [isFiltered, filteredQueryData, unfilteredQueryData]);

  const handleSwitchChange = (checked: boolean) => {
    setUseCustomRange(checked);
    if (!checked) {
      setStartDate(null);
      setEndDate(null);
    }
  };

  return (
    <div>
      <Form
        initialValues={{ timeRange: TimeSeriesRange.SinceVpd, currency: currencyId }}
        form={form}
      >
        <Row gutter={16}>
          <Col span={7}>
            <Form.Item label={`${PA_LABEL}`} name="practiceArea">
              <Cascader
                showSearch={true}
                allowClear
                placeholder={`Select ${PA_LABEL}`}
                options={paOptions}
                onChange={(value) => {
                  let selectedId = null;
                  if (Array.isArray(value) && value.length > 0) {
                    selectedId = value[value.length - 1];
                  } else if (typeof value === "number") {
                    selectedId = value;
                  }
                  setPracticeAreaId(selectedId !== null ? Number(selectedId) : null);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={7}>
            <Form.Item label="Office Location" name="officeLocation">
              <Cascader
                showSearch={true}
                allowClear
                placeholder="Select office location"
                options={olOptions}
                onChange={(value) =>
                  setOfficeLocationId(!isNaN(Number(value)) ? Number(value) : null)
                }
              />
            </Form.Item>
          </Col>
          <Col
            span={8}
            style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}
          >
            <div>
              {useCustomRange ? (
                <Form.Item label="Time Range">
                  <RangePicker
                    picker="month"
                    onChange={(dates) => {
                      if (dates) {
                        setStartDate(dates[0] ?? null);
                        setEndDate(dates[1] ?? null);
                      }
                    }}
                  />
                </Form.Item>
              ) : (
                <Form.Item label="Time Range" name="timeRange">
                  <Select onChange={(value) => setTimeRange(value)}>
                    {Object.keys(TimeSeriesRangeDisplayNames).map((key) => {
                      const timeRangeKey = key as keyof typeof TimeSeriesRange;
                      return (
                        <Select.Option key={key} value={TimeSeriesRange[timeRangeKey]}>
                          {TimeSeriesRangeDisplayNames[timeRangeKey]}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              )}
            </div>
            <Form.Item label="Custom Range" name="customRange" style={{ paddingLeft: 10 }}>
              <Switch onChange={handleSwitchChange} />
            </Form.Item>
          </Col>
        </Row>
        <Col span={3}>
          {isMultiCurrencyInstance && (
            <Form.Item label="Currency" name="currency">
              <Select onChange={(value) => setCurrencyId(value)} allowClear={true}>
                {currencies?.currencies?.map((currency) => (
                  <Select.Option key={currency.id} value={currency.id}>
                    {currency.code}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Col>
      </Form>
      <Roi
        loading={loadingFiltered || loadingUnfiltered}
        currencySymbol={currencySymbol ?? defaultCurrencySymbol}
        practiceAreaId={practiceAreaId}
        officeLocationId={officeLocationId}
        roiTimeSeriesData={roiTimeSeriesData}
        unfilteredRoiData={unfilteredRoiData}
        timeIntervals={timeIntervals}
      />
    </div>
  );
};

function parseRoiTimeSeriesData(dataJSON: string | null): RoiTimeSeriesData[] {
  if (!dataJSON) return [];
  try {
    const parsedData = JSON.parse(dataJSON);
    if (Array.isArray(parsedData) && parsedData.every(validateRoiTimeSeriesData)) {
      return parsedData;
    }
  } catch (error) {
    console.error("Failed to parse roiTimeSeriesDataJSON:", error);
  }
  return [];
}
